<template>
  <!-- usage -->
  <!-- <Calendar :range="false" :timePicker="false" v-model="selectedDate" /> -->
  <!-- then watch for the selectedDate model -->
  <!--  -->
  <section
    @click="openDatePicker"
    class="px-4 py-2 text-sm font-semibold text-gray-700 border border-gray-300 rounded-lg cursor-pointer w-fit bg-gray-50 "
    :class="[extraClass]"
  >
    <!-- Overlay -->
    <section
      v-show="isDatePickerVisible"
      class="fixed inset-0 z-50 w-full h-full bg-black bg-opacity-50"
      @click.prevent="closeDatePicker"
    ></section>
    <!--  -->

    <button class="w-full flex-between">
      <section class="flex items-center pr-2 gap-x-2">
        <img
          src="@/assets/svg/calendarSvg.svg"
          alt="date-picker"
          class="w-5 h-5"
        />
        <span>{{ formattedDate || translateText("calendar.selectDate") }}</span>
      </section>

      <i
        class="pi pi-times mt-[2px]"
        v-if="formattedDate"
        @click="resetCalendar"
      ></i>
    </button>

    <div v-show="isDatePickerVisible">
      <Teleport to="body">
        <VueDatePicker
          :range="range"
          :preset-dates="range ? presetDates : {}"
          teleport-center
          ref="datePicker"
          :enable-time-picker="time"
          :max-date="maxDate"
          :min-date="minDate"
          time-picker-inline
          v-model="internalDate"
          @blur="handleDatePickerBlur"
          :month-picker="monthPicker"
        />
      </Teleport>
    </div>
  </section>
</template>

<script>
//libraries
import moment from "moment";

//utils
import { formatDate, translateText, utcDate } from "@/core/helpers/utility";
import {
  endOfMonth,
  startOfWeek,
  endOfWeek,
  endOfYear,
  previousDay,
  startOfMonth,
  startOfYear,
  subMonths,
} from "date-fns";

export default {
  name: "Calendar",
  props: {
    modelValue: {
      default: null,
      type: [String, Array, Object],
    },

    range: {
      type: Boolean,
      default: false,
    },
    maxDate: {
      type: String,
      default: null,
    },
    minDate: {
      type: String,
      default: null,
    },
    time: {
      type: Boolean,
      default: false,
    },

    extraClass: {
      type: String,
      default: "",
    },

    openCalendar: {
      type: Boolean,
      default: false,
      Required: false,
    },
    monthPicker: {
      type: Boolean,
      default: false,
      Required: false,
    },
  },
  data() {
    return {
      utcDate,
      translateText,
      isDatePickerVisible: false,
      internalDate: null,
      timeFormat: this.time || this.showTime ? "lll" : "ll",
      presetDates: [
        { label: "Today", value: [new Date(), new Date()] },
        {
          label: "Today (Slot)",
          value: [new Date(), new Date()],
          slot: "preset-date-range-button",
        },
        {
          label: "Yesterday",
          value: [previousDay(new Date(), 1), previousDay(new Date(), 1)],
        },
        {
          label: "Last 7 days",
          value: [previousDay(new Date(), -5), new Date()],
        },
        {
          label: "Last week",
          value: [
            startOfWeek(previousDay(new Date(), -5)),
            startOfWeek(new Date()),
          ],
        },
        {
          label: "This month",
          value: [startOfMonth(new Date()), endOfMonth(new Date())],
        },
        {
          label: "Last month",
          value: [
            startOfMonth(subMonths(new Date(), 1)),
            endOfMonth(subMonths(new Date(), 1)),
          ],
        },
        {
          label: "This year",
          value: [startOfYear(new Date()), endOfYear(new Date())],
        },
      ],
    };
  },

  computed: {
    formattedDate() {
      const receivedDate = this.modelValue;
      if (!receivedDate) return null;

      if (receivedDate?.startDate) {
        const { startDate, endDate } = receivedDate;
        const formattedStartDate = moment(startDate).format(this.timeFormat);
        const formattedEndDate = moment(endDate).format(this.timeFormat);

        return `${formattedStartDate} - ${formattedEndDate}`;
      } else {
        return moment(receivedDate).format(this.timeFormat);
      }
    },
  },
  watch: {
    internalDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        const newDate = this.formatRange(newVal);
        this.$emit("update:modelValue", newDate);
      }
    },

    // modelValue(newVal) {
    //   if (newVal) {
    //     this.internalDate =
    //     this.formatRange(newVal);
    //   }
    // },

    openCalendar() {
      this.openCalendar && this.openDatePicker();
    },
  },

  methods: {
    formatRange(date) {
      if (!date) return null;

      if (Array.isArray(date)) {
        const newDate = {
          startDate: formatDate(date[0]),
          endDate: formatDate(date[1]),
        };

        return newDate;
      } else {
        return moment(date).format("YYYY-MM-DD hh:mm");
      }
    },
    openDatePicker() {
      this.isDatePickerVisible = true;

      this.$nextTick(() => {
        const input = this.$refs.datePicker.$el.querySelector("input");
        if (input) {
          input.click();
        }
      });
    },

    resetCalendar(e) {
      e.stopPropagation();
      this.internalDate = null;
      this.isDatePickerVisible = false;
      this.$emit("update:modelValue", null);
    },

    handleDatePickerBlur() {
      // Handle the blur event to close the date picker
      this.isDatePickerVisible = false;
    },
  },
};
</script>

<style></style>
